import {
  Avatar as MaterialAvatar,
  AvatarGroup as MaterialAvatarGroup,
} from '@mui/material';
import React from 'react';
import { useAchievements } from '../Context/AchievementsProvider';
import { User } from '../Types/User';
import { simpleHash } from '../Utils/General';

// Predefined colors for avatars
const AVATAR_COLORS = [
  '#1abc9c', // Turquoise
  '#2ecc71', // Emerald
  '#3498db', // Peter River
  '#9b59b6', // Amethyst
  '#34495e', // Wet Asphalt
  '#16a085', // Green Sea
  '#27ae60', // Nephritis
  '#2980b9', // Belize Hole
  '#8e44ad', // Wisteria
  '#2c3e50', // Midnight Blue
  '#f1c40f', // Sun Flower
  '#e67e22', // Carrot
  '#e74c3c', // Alizarin
  '#95a5a6', // Concrete
  '#f39c12', // Orange
  '#d35400', // Pumpkin
  '#c0392b', // Pomegranate
  '#7f8c8d', // Asbestos
];

const getColorFromString = (str: string): string => {
  const hash = simpleHash(str);
  const index = Math.abs(hash) % AVATAR_COLORS.length;
  return AVATAR_COLORS[index];
};

interface AvatarProps {
  user: User;
  src?: string;
  size?: number;
  className?: string;
  children?: React.ReactNode;
  onClick?: (event: React.MouseEvent) => void;
}

export const Avatar = ({
  user,
  src,
  size = 36,
  className,
  children,
  onClick,
}: AvatarProps) => {
  const { tierInfo, getTierColor } = useAchievements();
  const avatarSrc =
    src ?? user.profileImage?.iconUrl ?? user.profileImage?.originalUrl;

  const borderColor = tierInfo?.tier
    ? getTierColor(tierInfo.tier)
    : 'transparent';
  const borderWidth = size * 0.05; // 5% of size for border
  const indicatorSize = size * 0.35; // 20% of size for the indicator
  const avatarSize = size - 2 * borderWidth;

  const avatarProps = {
    sx: {
      ...(size && {
        width: avatarSize,
        height: avatarSize,
        fontSize: avatarSize / 2.5,
      }),
      bgcolor: !avatarSrc
        ? getColorFromString(`${user.name} ${user.surname}`)
        : undefined,
    },
    className,
  };

  const initials = `${user.name[0]}${user.surname[0]}`;

  return (
    <div
      style={{
        width: size,
        height: size,
        position: 'relative',
      }}
    >
      {tierInfo?.tier && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: indicatorSize,
            height: indicatorSize,
            borderRadius: '50%',
            border: `2px solid white`,
            backgroundColor: getTierColor(tierInfo.tier),
            zIndex: 1,
          }}
        />
      )}
      <div
        style={{
          width: '100%',
          height: '100%',
          borderRadius: '50%',
          padding: borderWidth,
          background: borderColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <MaterialAvatar
          {...avatarProps}
          src={avatarSrc}
          role="img"
          title={`${user.name} ${user.surname}`}
          alt={`${user.name} ${user.surname}`}
          onClick={onClick}
          aria-label={`${user.name} ${user.surname}'s avatar`}
          data-testid="user-avatar"
        >
          {children ?? initials}
        </MaterialAvatar>
      </div>
    </div>
  );
};

export const AvatarGroup = MaterialAvatarGroup;
